import { API } from '@/api'
import { getPrimaryTeamTokenData, getToken, isTokenExpired, setTeamToken } from '@/utils/auth'
import { getTeamIdFromStore } from '@/utils/tokenUtils'

// Promise cache to prevent concurrent refresh requests
let refreshPromise: Promise<string> | null = null

/**
 * Refresh team token using available tokens (primary team token or platform token)
 * Uses promise caching to prevent duplicate concurrent requests
 *
 * @returns New team token
 * @throws Error if refresh fails, 'PRIMARY_TOKEN_EXPIRED' if primary team token expired
 */
export async function refreshTeamToken(): Promise<string> {
  // Return existing promise if refresh is already in progress
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      // Use store-first approach to get team ID (fallback to cookie)
      const teamInfo = getTeamIdFromStore()

      if (!teamInfo) {
        throw new Error('No team ID or resID found')
      }

      const { teamId, teamResID } = teamInfo

      const primaryTeamData = getPrimaryTeamTokenData()

      // Check if we're trying to refresh the primary team itself
      if (primaryTeamData && primaryTeamData.teamId === teamId) {
        // This is the original login team
        // If its token is expired, we cannot refresh it with itself
        // User must re-login
        console.log('Primary team token expired, cannot self-refresh')
        throw new Error('PRIMARY_TOKEN_EXPIRED')
      }

      // Step 1: Try with Primary Team Token (for other teams)
      if (primaryTeamData && !isTokenExpired(primaryTeamData)) {
        try {
          const res = await API.getTeamToken(
            {
              privilege_type: 57,
              source_id: teamId,
            },
            {
              headers: {
                Authorization: primaryTeamData.token,
              },
              __skipTokenRefresh: true,
              errMsg: false,
            } as any,
          )

          console.log('Team token refreshed using primary team token')
          setTeamToken(`Bearer ${res.token}`, teamId, teamResID)
          return res.token
        } catch (error) {
          console.warn('Failed to refresh with primary token, trying platform token', error)
          // Fall through to platform token
        }
      }

      // Step 2: Try with Platform Token
      const platformToken = getToken()
      if (!platformToken) {
        throw new Error('No valid token available for refresh')
      }

      try {
        // Get new team token using platform token
        // Use __skipTokenRefresh flag to bypass auto-refresh logic and prevent infinite loops
        const res = await API.getTeamToken(
          {
            privilege_type: 57,
            source_id: teamId,
          },
          { __skipTokenRefresh: true, errMsg: false } as any,
        )

        console.log('Team token refreshed using platform token')
        setTeamToken(`Bearer ${res.token}`, teamId, teamResID)
        return res.token
      } catch (error) {
        console.error('Failed to refresh with platform token', error)
        throw error
      }
    } finally {
      // Clear promise cache after completion (with small delay to allow concurrent requests to resolve)
      setTimeout(() => {
        refreshPromise = null
      }, 100)
    }
  })()

  return refreshPromise
}

// Keep backward compatibility
export const refreshTeamTokenWithPlatformToken = refreshTeamToken

/**
 * Clear refresh promise cache (for testing or force refresh)
 */
export function clearRefreshCache(): void {
  refreshPromise = null
}
